import { useQuery } from "@corpuscomplete/zero-data-transformer/react";
import { memo, useState, type ReactNode } from "react";
import { Link } from "@corpuscomplete/web/components/link";
import { Page, PageBody, PageContent, PageHeader } from "@corpuscomplete/web/components/layout";
import { Button } from "@corpuscomplete/web-ui/components/button";
import { Badge } from "@corpuscomplete/web-ui/components/badge";
import {
  Empty,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
  EmptyDescription,
} from "@corpuscomplete/web-ui/components/empty";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@corpuscomplete/web-ui/components/dialog";
import {
  ContextMenu,
  ContextMenuTrigger,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuSeparator,
} from "@corpuscomplete/web-ui/components/context-menu";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@corpuscomplete/web-ui/components/dropdown-menu";
import { Label } from "@corpuscomplete/web-ui/components/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@corpuscomplete/web-ui/components/table";
import { Tabs, TabsList, TabsTrigger, TabsPanel } from "@corpuscomplete/web-ui/components/tabs";
import { Text } from "@corpuscomplete/web-ui/components/text";
import { queries } from "@corpuscomplete/core/zero";
import { Nutrient } from "@corpuscomplete/core/nutrition/nutrient";
import { NutrientUI } from "@corpuscomplete/core-ui/nutrition/nutrient";
import type { Recipe } from "@corpuscomplete/core/nutrition/recipe";
import { BookOpenIcon, ChevronDownIcon } from "lucide-react";
import { useAuth } from "@corpuscomplete/web/auth/hooks";
import { FoodCollectionDetail } from "@corpuscomplete/core-ui/nutrition/food-collection/detail";
import { Scope } from "@corpuscomplete/core/common/scope";

/**
 * Food collection detail route boundary.
 * Contract: binds route params/auth identity into the
 * FoodCollectionDetail core-ui provider and renders collection-level views.
 */
export function FoodCollectionDetailPage({ id }: { id: string }) {
  const { userId } = useAuth();

  return (
    <FoodCollectionDetail.DataProvider collectionId={id} userId={userId}>
      <FoodCollectionDetailContent userId={userId} />
    </FoodCollectionDetail.DataProvider>
  );
}

function FoodCollectionDetailContent({ userId }: { userId: string }) {
  const collection = FoodCollectionDetail.useCollection();
  const { isLoading } = FoodCollectionDetail.useLoadingState();
  const { canMoveContent } = FoodCollectionDetail.usePermissions();

  if (isLoading) {
    return null;
  }

  if (!collection) {
    return (
      <Page>
        <PageBody>
          <PageHeader>
            <Text variant="sm">Collection</Text>
          </PageHeader>
          <PageContent>
            <Empty className="flex-1">
              <EmptyHeader>
                <EmptyTitle>Collection not found</EmptyTitle>
                <EmptyDescription>
                  This collection may have been deleted or you don't have access.
                </EmptyDescription>
              </EmptyHeader>
            </Empty>
          </PageContent>
        </PageBody>
      </Page>
    );
  }
  const scope = Scope.fromId({ scopeId: collection.scopeId, userId });

  return (
    <Page>
      <PageBody>
        <PageHeader>
          <div className="mr-auto flex items-center gap-3">
            <div className="flex items-center gap-2">
              <Text variant="sm">{collection.name}</Text>
              {collection.isDefault && <Badge variant="secondary">Default</Badge>}
            </div>
          </div>
        </PageHeader>
        <PageContent>
          <Tabs defaultValue="recipes">
            <TabsList>
              <TabsTrigger value="recipes">Recipes</TabsTrigger>
            </TabsList>
            <TabsPanel value="recipes">
              <RecipesTab canMove={canMoveContent} scope={scope} />
            </TabsPanel>
          </Tabs>
        </PageContent>
      </PageBody>
    </Page>
  );
}

function RecipesTab({ canMove, scope }: { canMove: boolean; scope: Scope.Info }) {
  const recipes = FoodCollectionDetail.useFoods();
  const { isLoading } = FoodCollectionDetail.useLoadingState();
  const [moveRecipe, setMoveRecipe] = useState<{
    id: string;
    name: string;
    foodId: string;
  } | null>(null);

  if (isLoading && recipes.length === 0) {
    return null;
  }

  if (recipes.length === 0) {
    return (
      <Empty className="flex-1 py-12">
        <EmptyHeader>
          <EmptyMedia>
            <div className="flex size-16 items-center justify-center rounded-full bg-muted">
              <BookOpenIcon className="size-8 text-muted-foreground" />
            </div>
          </EmptyMedia>
          <EmptyTitle>No recipes in this collection</EmptyTitle>
          <EmptyDescription>
            Create recipes from the Recipes page to add them here.
          </EmptyDescription>
        </EmptyHeader>
        <RecipesLink scope={scope}>
          <Button variant="outline">Go to Recipes</Button>
        </RecipesLink>
      </Empty>
    );
  }

  return (
    <>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-[100px]">Name</TableHead>
            <TableHead>Yield</TableHead>
            <TableHead>Nutrients per Serving</TableHead>
            <TableHead className="text-right">Prep Time</TableHead>
            <TableHead className="text-right">Cook Time</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {recipes.map((recipe) => (
            <RecipeTableRow
              key={recipe.id}
              recipe={recipe}
              canMove={canMove}
              onMove={() => {
                if (recipe.base) {
                  setMoveRecipe({
                    id: recipe.id,
                    name: recipe.base.name,
                    foodId: recipe.base.id,
                  });
                }
              }}
            />
          ))}
        </TableBody>
      </Table>
      {moveRecipe && (
        <MoveRecipeDialog
          recipe={moveRecipe}
          open={true}
          onOpenChange={(open) => {
            if (!open) setMoveRecipe(null);
          }}
        />
      )}
    </>
  );
}

const RecipeTableRow = memo(
  ({
    recipe,
    canMove,
    onMove,
  }: {
    recipe: Recipe.ListItem;
    canMove: boolean;
    onMove: () => void;
  }) => {
    const { nutrientKeys } = NutrientUI.useDisplayNutrients();
    const [nutrients] = useQuery(queries.nutrient.byKeys(nutrientKeys));

    if (!recipe.serving?.base || !recipe.base) {
      return null;
    }

    const amounts = Nutrient.addFoodItems(Nutrient.emptyAmounts(), [
      { amount: 1, serving: recipe.serving.base },
    ]);
    const nutrientAmounts = Nutrient.amountsToInfo(amounts, nutrients, nutrientKeys);

    return (
      <ContextMenu>
        <ContextMenuTrigger>
          <TableRow>
            <TableCell className="font-medium">
              <RecipeLink id={recipe.id} className="hover:underline">
                {recipe.base.name}
              </RecipeLink>
            </TableCell>
            <TableCell>
              {recipe.serving.perRecipe} {recipe.serving.base.name}
            </TableCell>
            <TableCell>
              {nutrientAmounts.map(({ nutrient, amount }) => (
                <div key={nutrient.id}>
                  {nutrient.defaultName}: {amount.toFixed(1)} {nutrient.unit?.defaultName}
                </div>
              ))}
            </TableCell>
            <TableCell className="text-right">
              {recipe.prepTimeMinutes ? `${recipe.prepTimeMinutes}mins` : "N/A"}
            </TableCell>
            <TableCell className="text-right">
              {recipe.cookTimeMinutes ? `${recipe.cookTimeMinutes}mins` : "N/A"}
            </TableCell>
          </TableRow>
        </ContextMenuTrigger>
        <ContextMenuContent>
          <RecipeLink id={recipe.id}>
            <ContextMenuItem>
              <Text variant="sm">View</Text>
            </ContextMenuItem>
          </RecipeLink>
          {canMove && (
            <>
              <ContextMenuSeparator />
              <ContextMenuItem onClick={onMove}>
                <Text variant="sm">Move to...</Text>
              </ContextMenuItem>
            </>
          )}
        </ContextMenuContent>
      </ContextMenu>
    );
  },
);

function RecipesLink({ children, scope }: { children: ReactNode; scope: Scope.Info }) {
  if (scope.kind === "personal") {
    return <Link to="/personal/recipes">{children}</Link>;
  }

  return (
    <Link to="/spaces/$spaceId/recipes" params={{ spaceId: scope.spaceId }}>
      {children}
    </Link>
  );
}

function RecipeLink({
  children,
  className,
  id,
}: {
  children: ReactNode;
  className?: string;
  id: string;
}) {
  return (
    <Link to="/recipes/$id" params={{ id }} className={className}>
      {children}
    </Link>
  );
}

function MoveRecipeDialog({
  recipe,
  open,
  onOpenChange,
}: {
  recipe: { id: string; name: string; foodId: string };
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const availableCollections = FoodCollectionDetail.useAvailableCollections();
  const { moveFood } = FoodCollectionDetail.useMutations();
  const [selectedCollectionId, setSelectedCollectionId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const selectedCollection = availableCollections.find((c) => c.id === selectedCollectionId);

  const handleMove = async () => {
    if (!selectedCollectionId) return;

    try {
      const { client } = moveFood(recipe.foodId, selectedCollectionId);
      const result = await client;
      if (result.type === "error") {
        setError(result.error.message);
        return;
      }
      onOpenChange(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to move recipe");
    }
  };

  const handleOpenChange = (isOpen: boolean) => {
    if (!isOpen) {
      setError(null);
      setSelectedCollectionId(null);
    }
    onOpenChange(isOpen);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Move Recipe</DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-4 py-4">
          <Text variant="sm">Move "{recipe.name}" to:</Text>
          <div className="flex flex-col gap-2">
            <Label>Collection</Label>
            <DropdownMenu>
              <DropdownMenuTrigger
                render={<Button variant="outline" className="justify-between" />}
              >
                <Text variant="sm">{selectedCollection?.name ?? "Select a collection"}</Text>
                <ChevronDownIcon className="size-4" />
              </DropdownMenuTrigger>
              <DropdownMenuContent>
                {availableCollections.map((collection) => (
                  <DropdownMenuItem
                    key={collection.id}
                    onClick={() => setSelectedCollectionId(collection.id)}
                  >
                    <Text variant="sm">{collection.name}</Text>
                  </DropdownMenuItem>
                ))}
                {availableCollections.length === 0 && (
                  <DropdownMenuItem disabled>
                    <Text variant="sm" className="text-muted-foreground">
                      No other collections available
                    </Text>
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
          {error && (
            <Text variant="sm" className="text-destructive">
              {error}
            </Text>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" size="sm" onClick={() => handleOpenChange(false)}>
            Cancel
          </Button>
          <Button size="sm" onClick={handleMove} disabled={!selectedCollectionId}>
            Move
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
